/**
 * Choix du manifest PWA selon la page ouverte.
 *
 * Il n'y avait qu'UN manifest pour tout le site, avec `start_url: /shop` : quand
 * Philippe ajoutait `/accueil` à l'écran d'accueil de son iPad, Safari lisait
 * ce manifest et l'icône ouvrait… la boutique. Le nom sous l'icône était aussi
 * celui de la boutique.
 *
 * Trois manifests, choisis par le chemin :
 *  - boutique (celui généré par vite-plugin-pwa, `start_url: /shop`) pour tout
 *    ce que voit un client ;
 *  - « Folies Caisse » (`start_url: /pos`) pour les pages vendeur par PIN
 *    (caisse, stocks, réserve, réception, préparation) — un iPad de comptoir
 *    ajouté depuis `/pos` doit rouvrir la caisse, pas un accueil réservé aux
 *    rôles ADMIN/MANAGER ;
 *  - « Folies Équipe » (`start_url: /accueil`) pour tout le reste de l'espace
 *    équipe (accueil tactile, connexion, tableau de bord).
 *
 * ⚠️ Ce commutateur ne suffit PAS sur iPhone/iPad : WebKit récupère le manifest
 * PENDANT le chargement du document et ignore un `href` changé ensuite —
 * l'icône posée depuis /accueil ouvrait toujours la boutique. Le bon manifest
 * est donc écrit dans le document lui-même pour les deux pages qu'on installe
 * (`/accueil` et `/pos`), par `sectionEntryPages()` dans `vite.config.ts`.
 * Ce commutateur reste utile aux navigateurs qui relisent le manifest au moment
 * d'installer (Chrome/Android) et aux autres pages de l'espace équipe.
 */

export type PwaManifestKind = 'boutique' | 'caisse' | 'equipe';

export interface PwaManifestChoice {
  kind: PwaManifestKind;
  href: string;
  /** Nom sous l'icône iOS (`apple-mobile-web-app-title`). */
  appleTitle: string;
}

const CLIENT_PREFIXES = /^\/(shop|sales|a|live-shopping|checkout|account)(\/|$)/;
const CAISSE_PREFIXES = /^\/(pos|stocks|storage|reception|preparation|executor|welcome)(\/|$)/;

export function pwaManifestKindFor(pathname: string): PwaManifestKind {
  if (pathname === '/' || CLIENT_PREFIXES.test(pathname)) return 'boutique';
  // Écran client 7" face à la caisse : rien à installer, il reste « boutique ».
  if (pathname.startsWith('/pos/affichage-client')) return 'boutique';
  if (CAISSE_PREFIXES.test(pathname)) return 'caisse';
  return 'equipe';
}

export function pwaManifestFor(pathname: string): PwaManifestChoice {
  const kind = pwaManifestKindFor(pathname);
  switch (kind) {
    case 'caisse':
      return { kind, href: '/manifest-caisse.webmanifest', appleTitle: 'Folies Caisse' };
    case 'equipe':
      return { kind, href: '/manifest-equipe.webmanifest', appleTitle: 'Folies Équipe' };
    default:
      // Manifest généré au build par vite-plugin-pwa (`manifest.webmanifest`).
      return { kind, href: '/manifest.webmanifest', appleTitle: 'Folies Maraîchères' };
  }
}

/** Applique le choix au document (idempotent). */
export function applyPwaManifest(pathname: string): PwaManifestChoice {
  const choice = pwaManifestFor(pathname);
  if (typeof document === 'undefined') return choice;

  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== choice.href) link.setAttribute('href', choice.href);

  const title = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (title && title.content !== choice.appleTitle) title.content = choice.appleTitle;

  return choice;
}
